/**
 * Assistant API service
 *
 * POST /assistant/chat — non-streaming. Real path uses the shared axios
 * `api` instance (Cognito token interceptor + session expiry handling).
 *
 * Mock mode is controlled by REACT_APP_ASSISTANT_MOCK. Default is OFF in
 * production-style builds (UAT/prod) and ON in local dev. Mocked responses
 * conform to the backend AssistantChatResponse contract exactly so the
 * UI sees a real shape, not a half-baked dev fixture.
 */

import { api } from './api'
import type {
  AssistantChatRequest,
  AssistantChatResponse,
  CapExceededErrorBody,
} from '../types/assistant'

/** Local-dev defaults true; UAT/prod env explicitly sets to 'false' or omits. */
const MOCK_MODE = process.env.REACT_APP_ASSISTANT_MOCK === 'true'

/**
 * Dedicated error class so the slice can branch on cap-exceeded without
 * regex-matching error messages.
 */
export class AssistantCapExceededError extends Error {
  readonly body: CapExceededErrorBody
  constructor(body: CapExceededErrorBody) {
    super(body.message ?? `Daily ${body.cap} cap exceeded`)
    this.name = 'AssistantCapExceededError'
    this.body = body
  }
}

export async function sendAssistantMessage(
  req: AssistantChatRequest
): Promise<AssistantChatResponse> {
  if (MOCK_MODE) {
    return mockSendAssistantMessage(req)
  }

  try {
    const response = await api.post<AssistantChatResponse>('/assistant/chat', req)
    return response.data
  } catch (err: any) {
    // Surface 429 cap-exceeded as a typed error
    if (err?.response?.status === 429 && err.response?.data?.error === 'LIMIT_EXCEEDED') {
      throw new AssistantCapExceededError(err.response.data as CapExceededErrorBody)
    }
    throw err
  }
}

// ============================================================================
// Mock implementation
// ============================================================================

/**
 * Server-style mock: tracks a per-tab conversationId and synthetic capacity.
 * Latency simulated with a small delay so loading states are visible during
 * dev. Keeps response shape byte-identical to the real backend contract.
 */
let mockConvId: string | null = null
let mockTurnsLeft = 40

async function mockSendAssistantMessage(
  req: AssistantChatRequest
): Promise<AssistantChatResponse> {
  await delay(450 + Math.random() * 350)

  if (mockTurnsLeft <= 0) {
    throw new AssistantCapExceededError({
      error: 'LIMIT_EXCEEDED',
      cap: 'turns',
      limit: 40,
      resetAt: nextUtcMidnight(),
      message: 'Daily turn cap exceeded (limit 40)',
    })
  }

  const conversationId = req.conversationId ?? mockConvId ?? mintMockConvId()
  if (!req.conversationId) mockConvId = conversationId
  mockTurnsLeft -= 1

  return {
    conversationId,
    message: { text: mockReplyText(req.message) },
    attachments: [],
    capacityRemaining: {
      turns: mockTurnsLeft,
      premiumTokens: 0,
      reports: 20,
      resetAt: nextUtcMidnight(),
    },
    usage: {
      inputTokens: 1000 + Math.floor(Math.random() * 500),
      outputTokens: 100 + Math.floor(Math.random() * 200),
      cachedInputTokens: 0,
      totalTokens: 1100,
      totalCostMicrosUsd: 1500,
    },
  }
}

function mockReplyText(userMessage: string): string {
  const lower = userMessage.toLowerCase()
  if (lower.includes('hello') || lower.includes('hi')) {
    return 'Hello! I can help you research Dealigent property deals. Try asking about BTL opportunities in Manchester or HMO deals under £300k.'
  }
  if (lower.includes('manchester') || lower.includes('btl')) {
    return `(Mock) I found some Manchester BTL deals. Once attachments ship in slice 2, you'll see property cards here.`
  }
  if (lower.includes('compare')) {
    return `(Mock) Comparison view will render side-by-side property cards once slice 2 ships attachment renderers.`
  }
  return `(Mock) Got your message: "${userMessage.slice(0, 80)}". Real responses arrive after backend deployment in slice 3.`
}

function mintMockConvId(): string {
  return `conv_mock_${Math.random().toString(36).slice(2, 10)}`
}

function nextUtcMidnight(): string {
  const now = new Date()
  const next = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
    0, 0, 0, 0
  ))
  return next.toISOString()
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Test/dev helper. Not used in production code paths. */
export function __resetMockState(): void {
  mockConvId = null
  mockTurnsLeft = 40
}
